/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import {
  Tbody,
  Tr,
  Td,
  Button,
  Stack,
  Tag,
  useDisclosure,
  IconButton,
  Tooltip
} from '@chakra-ui/react'
import { PiPencilDuotone } from 'react-icons/pi'
import { TiDeleteOutline } from 'react-icons/ti'
import { getTagProperties } from './handlers'
import EditModal from './components/EditModal'
import DeleteModal from './components/DeleteModal'
import { formatSeparateIngredientsValue } from '../../../../handlers'

const CustomTableBody = ({
  columns,
  items,
  onRetry,
  isHiddenForm,
  onEdit,
  onDelete,
  batchId,
  aiStrategies
}) => {
  const [currentItem, setCurrentItem] = useState(null)
  const [itemToDelete, setItemToDelete] = useState(null)
  const options = aiStrategies?.map(
    strategy => ({
      id: strategy.id,
      name: strategy.name
    })
  )

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose
  } = useDisclosure()

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose
  } = useDisclosure()

  const ColumnContent = ({ item, column }) => {
    const { id } = column

    if (id === 'state') {
      const {
        label,
        colorScheme
      } = getTagProperties(item[id])

      return (
        <Tag colorScheme={colorScheme}>
          { label }
        </Tag>
      )
    }

    if (id === 'embeddingModelId') {
      return options
        ?.find(
          i => i.id === parseInt(item[id])
        )
        ?.name
    }

    if (id === 'separateIngredients') {
      return formatSeparateIngredientsValue(
        item.fileType,
        item.separateIngredients
      )
    }

    return Array.isArray(item[id]) ? item[id].join() : item[id]
  }

  const handleOnRetry = (item) => {
    onRetry && onRetry(item)
  }

  const handleEdit = (item) => {
    setCurrentItem(item)
    onEditOpen()
  }

  const handleDelete = (item) => {
    setItemToDelete({ ...item })
    setTimeout(() => onDeleteOpen(), 0)
  }

  const handleCloseDeleteModal = (confirmed = false) => {
    const itemToBeDeleted = itemToDelete
    onDeleteClose()
    if (confirmed && itemToBeDeleted && onDelete) {
      onDelete(itemToBeDeleted)
    }
    setItemToDelete(null)
  }

  const handleCloseEditModal = () => {
    onEditClose()
    setCurrentItem(null)
  }

  const handleSaveEdit = (updatedItem) => {
    onEdit && onEdit(updatedItem)
    handleCloseEditModal()
  }

  return (
    <>
      <EditModal
        isOpen={isEditOpen}
        onClose={handleCloseEditModal}
        item={currentItem}
        onSave={handleSaveEdit}
        aiStrategies={aiStrategies}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={(success) => handleCloseDeleteModal(success)}
        item={itemToDelete}
        batchId={batchId}
      />

      <Tbody>
        {
          items?.map(item => (
            <Tr
              key={item.id}
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              {columns.map(column => (
                <Td key={column.id}>
                  <ColumnContent item={item} column={column}/>
                </Td>
              ))}
              <Td>
                <Stack spacing={3} direction="row" align="center">
                  <Tooltip label="Edit">
                    <IconButton
                      aria-label="Edit asset"
                      icon={<PiPencilDuotone />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleEdit(item)}
                    />
                  </Tooltip>

                  <Tooltip label="Delete">
                    <IconButton
                      aria-label="Delete asset"
                      icon={<TiDeleteOutline />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDelete(item)}
                    />
                  </Tooltip>

                  { item.state === 'FAILED' && onRetry && isHiddenForm &&
                    <Button
                      size="sm"
                      onClick={() => handleOnRetry(item)}
                    >
                      Retry
                    </Button>
                  }
                </Stack>
              </Td>
            </Tr>
          ))}
      </Tbody>
    </>
  )
}

export default CustomTableBody
